import { STATUS } from "@/constants/statuses";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import ReviewsListResponseEntity from "@apps/reviews/entities/ReviewsLIstResponseEntity";
import ReviewsListResponseSchema from "@apps/reviews/schemas/ReviewsListResponseSchema";
import ReviewsStoreBase from "@apps/reviews/stores/ReviewsStoreBase";
import { applyTransaction } from "@datorama/akita";
import { ApiClientQueryParams, ApiClientResponse } from "@lib/ApiClient";
import ApiClientInterface from "@lib/ApiClient/ApiClientInterface";
import { Observable } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { create } from "superstruct";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import ReviewEntity from "../entities/ReviewEntity";
import ReviewsSortingOptionsEntity from "../entities/ReviewsSortingOptionsEntity";
import VotesStore from "../stores/VotesStore";

export type GetReviewsParamsDTO = {
    productId?: number;
    offset?: number;
    sortingOptions?: ReviewsSortingOptionsEntity[] | string;
    search?: string;
    onlyShopReviews?: boolean;
    onlyProductReviews?: boolean;
    onlyPinnedReviews?: boolean;
    onlyWithPhotos?: boolean;
    lowestRating?: number;
    perPage?: number;
    reviewIds?: number[];
};

export type GetReviewsWithFiltersParamsDTO = GetReviewsParamsDTO &
    ApiClientQueryParams;

type CustomAttributeIds = string | number;

export type GetReviewsRequestParamsDTO = GetReviewsParamsDTO & {
    customAttributeIds?: CustomAttributeIds;
};

export type LeaveReviewRequestParamsDTO = {
    productId?: number;
    title?: string;
    body: string;
    rating: number;
    images?: any[];
    guestEmail?: string;
    guestName?: string;
    widget?: REVIEWS_WIDGET_NAMES;
    reviewRequestToken?: string;
};

class ReviewsServiceBase {
    constructor(
        protected reviewsStore: ReviewsStoreBase,
        protected votesStore: VotesStore,
        protected readonly authApiClient: ApiClientInterface,
        protected readonly logger: LoggerInterface
    ) {}

    protected getReviewsRequest(
        reqParams?: GetReviewsWithFiltersParamsDTO
    ): Observable<ApiClientResponse<unknown>> {
        this.logger.debug(
            "ReviewsService.getReviewsRequest is started",
            reqParams
        );

        return this.authApiClient.get({
            url: "/review/getReviewList",
            responseType: "json",
            queryParams: reqParams,
        });
    }

    getReviews(
        reqParams?: GetReviewsWithFiltersParamsDTO
    ): Observable<ReviewsListResponseEntity> {
        this.reviewsStore.setStatus(STATUS.INITIAL_LOADING);

        return this.getReviewsRequest(reqParams).pipe(
            map((v) => create(v.body, ReviewsListResponseSchema)),
            catchError((error: unknown) => {
                this.logger.debug(
                    "ReviewsService.getReviewsRequest is errored",
                    { reqParams, error }
                );
                applyTransaction(() => {
                    this.reviewsStore.setError(error);
                    this.reviewsStore.setStatus(STATUS.ERROR);
                });
                throw error;
            }),
            tap((v) => {
                this.logger.debug(
                    "ReviewsService.getReviewsRequest is ready",
                    reqParams
                );
                applyTransaction(() => {
                    this.reviewsStore.set(v.items);
                    this.votesStore.add(
                        v.items.map((item) => ({
                            reviewId: item.id,
                            votes: item.votes,
                            isCustomerVoted: item.isCustomerVoted,
                            status: STATUS.VIRGIN,
                        }))
                    );
                    this.reviewsStore.update(() => ({
                        paginationMeta: {
                            perPage: v.perPage,
                            totalCount: v.totalCount,
                            currentOffset: v.currentOffset,
                        },
                    }));
                    this.reviewsStore.setStatus(STATUS.READY);
                });
            })
        );
    }

    loadMoreReviews(
        reqParams: GetReviewsWithFiltersParamsDTO
    ): Observable<ReviewsListResponseEntity> {
        this.reviewsStore.setStatus(STATUS.LOADING);

        return this.getReviewsRequest(reqParams).pipe(
            map((v) => create(v.body, ReviewsListResponseSchema)),
            catchError((error: unknown) => {
                this.logger.debug(
                    "ReviewsService.getReviewsRequest is errored",
                    reqParams
                );
                applyTransaction(() => {
                    this.reviewsStore.setError(error);
                    this.reviewsStore.setStatus(STATUS.ERROR);
                });
                throw error;
            }),
            tap((v) => {
                this.logger.debug(
                    "ReviewsService.getReviewsRequest is ready",
                    reqParams
                );
                applyTransaction(() => {
                    this.reviewsStore.add(v.items);
                    this.votesStore.add(
                        v.items.map((item) => ({
                            reviewId: item.id,
                            votes: item.votes,
                            isCustomerVoted: item.isCustomerVoted,
                            status: STATUS.VIRGIN,
                        }))
                    );
                    this.reviewsStore.update(() => ({
                        paginationMeta: {
                            perPage: v.perPage,
                            totalCount: v.totalCount,
                            currentOffset: v.currentOffset,
                        },
                    }));

                    this.reviewsStore.setStatus(STATUS.READY);
                });
            })
        );
    }

    appendReview(review: ReviewEntity) {
        applyTransaction(() => {
            this.reviewsStore.add(review, { prepend: true });
            this.reviewsStore.update((state) => ({
                ...state,
                paginationMeta: {
                    ...state.paginationMeta,
                    totalCount: state.paginationMeta.totalCount + 1,
                },
            }));
        });
    }

    updateReview(review: ReviewEntity) {
        applyTransaction(() => {
            const state = this.reviewsStore.getValue();
            if (!state.entities) return;
            const existingReview = Object.values(state.entities).find(
                (entity) => entity.id === review.id
            );
            if (existingReview) {
                this.reviewsStore.update(
                    existingReview.uid,
                    (state: ReviewEntity) => ({
                        ...state,
                        isCustomerAuthor: false,
                    })
                );
            }
            this.reviewsStore.add(review, { prepend: true });
        });
    }
}

export default ReviewsServiceBase;
